import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { useCallback, useMemo, useRef, useEffect } from 'react';
import { addItemToArray } from 'ariakit-utils/array';
import { getDocument } from 'ariakit-utils/dom';
import { useControlledState } from 'ariakit-utils/hooks';

function isElementPreceding(a, b) {
  return Boolean(b.compareDocumentPosition(a) & Node.DOCUMENT_POSITION_PRECEDING);
}

function findDOMIndex(items, item) {
  var itemElement = item.ref.current;
  if (!itemElement) return -1;
  var length = items.length;
  if (!length) return -1; // Most of the times, the new item will be added at the end of the list, so we
  // do a findeIndex in reverse order, instead of wasting time searching the
  // index from the beginning.

  while (length--) {
    var currentItem = items[length];
    if (!(currentItem != null && currentItem.ref.current)) continue;

    if (isElementPreceding(currentItem.ref.current, itemElement)) {
      return length + 1;
    }
  }

  return 0;
}

function sortBasedOnDOMPosition(items) {
  var pairs = items.map(function (item, index) {
    return [index, item];
  });
  var isOrderDifferent = false;
  pairs.sort(function (_ref, _ref2) {
    var _ref5 = _slicedToArray(_ref, 2),
        indexA = _ref5[0],
        a = _ref5[1];

    var _ref6 = _slicedToArray(_ref2, 2),
        indexB = _ref6[0],
        b = _ref6[1];

    var elementA = a.ref.current;
    var elementB = b.ref.current;
    if (elementA === elementB) return 0;
    if (!elementA || !elementB) return 0; // a before b

    if (isElementPreceding(elementA, elementB)) {
      if (indexA > indexB) {
        isOrderDifferent = true;
      }

      return -1;
    } // a after b


    if (indexA < indexB) {
      isOrderDifferent = true;
    }

    return 1;
  });

  if (isOrderDifferent) {
    return pairs.map(function (_ref3) {
      var _ref7 = _slicedToArray(_ref3, 2),
          _ = _ref7[0],
          item = _ref7[1];

      return item;
    });
  }

  return items;
}

function setItemsBasedOnDOMPosition(items, setItems) {
  var sortedItems = sortBasedOnDOMPosition(items);

  if (items !== sortedItems) {
    setItems(sortedItems);
  }
}

function getCommonParent(items) {
  var _firstItem$ref$curren;

  var firstItem = items[0];
  var lastItem = items[items.length - 1];
  var parentElement = firstItem == null ? void 0 : (_firstItem$ref$curren = firstItem.ref.current) == null ? void 0 : _firstItem$ref$curren.parentElement;

  while (parentElement) {
    var parent = parentElement;

    if (lastItem && parent.contains(lastItem.ref.current)) {
      return parentElement;
    }

    parentElement = parentElement.parentElement;
  }

  return getDocument(parentElement).body;
}

function useTimeoutObserver(items, setItems) {
  useEffect(function () {
    var callback = function callback() {
      return setItemsBasedOnDOMPosition(items, setItems);
    };

    var timeout = setTimeout(callback);
    return function () {
      return clearTimeout(timeout);
    };
  });
}

function useSortBasedOnDOMPosition(items, setItems) {
  // istanbul ignore else: JSDOM doesn't support IntersectionObverser
  // See https://github.com/jsdom/jsdom/issues/2032
  if (typeof IntersectionObserver !== "function") {
    useTimeoutObserver(items, setItems);
    return;
  }

  var previousItems = useRef([]);
  useEffect(function () {
    var callback = function callback() {
      var hasPreviousItems = !!previousItems.current.length;
      previousItems.current = items; // We don't want to sort items if items have been just registered.

      if (!hasPreviousItems) return;
      setItemsBasedOnDOMPosition(items, setItems);
    };

    var root = getCommonParent(items);
    var observer = new IntersectionObserver(callback, {
      root: root
    });
    items.forEach(function (item) {
      if (item.ref.current) {
        observer.observe(item.ref.current);
      }
    });
    return function () {
      return observer.disconnect();
    };
  }, [items, setItems]);
}
/**
 * Provides state for the `Collection` components.
 * @see https://ariakit.org/components/collection
 * @example
 * ```jsx
 * const collection = useCollectionState();
 * <Collection state={collection}>
 *   <CollectionItem>Item 1</CollectionItem>
 *   <CollectionItem>Item 2</CollectionItem>
 *   <CollectionItem>Item 3</CollectionItem>
 * </Collection>
 * ```
 */


function useCollectionState(props) {
  if (props === void 0) {
    props = {};
  }

  var _useControlledState = useControlledState([], props.items, props.setItems),
      _useControlledState2 = _slicedToArray(_useControlledState, 2),
      items = _useControlledState2[0],
      setItems = _useControlledState2[1];

  useSortBasedOnDOMPosition(items, setItems);
  var registerItem = useCallback(function (item) {
    setItems(function (prevItems) {
      // Finds the item group based on the DOM hierarchy
      var index = findDOMIndex(prevItems, item);
      return addItemToArray(prevItems, item, index);
    });

    var unregisterItem = function unregisterItem() {
      setItems(function (prevItems) {
        var nextItems = prevItems.filter(function (_ref4) {
          var ref = _ref4.ref;
          return ref !== item.ref;
        });

        if (prevItems.length === nextItems.length) {
          // The item isn't registered, so do nothing
          return prevItems;
        }

        return nextItems;
      });
    };

    return unregisterItem;
  }, []);
  var state = useMemo(function () {
    return {
      items: items,
      setItems: setItems,
      registerItem: registerItem
    };
  }, [items, setItems, registerItem]);
  return state;
}

export { useCollectionState };